import React, { useEffect, useMemo, useState } from 'react';
import {
  Bookmark,
  ExternalLink,
  Globe2,
  TrendingUp,
  X,
} from 'lucide-react';
import { usePageContext } from '../contexts';
import { fetchNewsArticles } from '../services/newsService';

const STORAGE_KEYS = {
  saved: 'panometra-news-saved-articles',
  cache: 'panometra-news-live-cache',
};

const NEWS_CACHE_MAX_AGE_MS = 5 * 60 * 1000;

const readStoredArray = (key, fallback) => {
  if (typeof window === 'undefined') return fallback;

  try {
    const stored = window.localStorage.getItem(key);
    if (!stored) return fallback;
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed : fallback;
  } catch {
    return fallback;
  }
};

const readStoredNewsCache = () => {
  if (typeof window === 'undefined') return [];

  try {
    const stored = window.sessionStorage.getItem(STORAGE_KEYS.cache);
    if (!stored) return [];

    const parsed = JSON.parse(stored);
    const isFresh = Date.now() - Number(parsed.cachedAt || 0) < NEWS_CACHE_MAX_AGE_MS;
    return isFresh && Array.isArray(parsed.articles) ? parsed.articles : [];
  } catch {
    return [];
  }
};

const writeStoredNewsCache = (articles) => {
  if (typeof window === 'undefined' || !articles.length) return;

  try {
    window.sessionStorage.setItem(STORAGE_KEYS.cache, JSON.stringify({
      cachedAt: Date.now(),
      articles,
    }));
  } catch {
    // Ignore storage quota/private-mode errors.
  }
};

const formatTimeAgo = (value) => {
  const timestamp = new Date(value).getTime();
  if (Number.isNaN(timestamp)) return 'Recently';

  const minutes = Math.max(0, Math.floor((Date.now() - timestamp) / 60000));
  if (minutes < 1) return 'Now';
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

const formatDate = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unknown date';

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
};

const getArticleHaystack = (article) => [
  article.title,
  article.summary,
  article.source,
  article.sourceDomain,
  article.category,
  article.region,
  ...(article.tags || []),
  ...(article.entities || []),
].join(' ').toLowerCase();

const toTitleCase = (value) => (
  String(value || '')
    .replace(/[-_]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase())
);

const buildTrendingTopics = (articles) => {
  const topicsByKey = new Map();

  const addTopic = (rawName, article, type = 'Topic') => {
    const name = String(rawName || '').trim();
    if (!name || name.length < 2) return;

    const key = name.toLowerCase();
    const current = topicsByKey.get(key) || {
      id: key.replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''),
      name: toTitleCase(name),
      query: name,
      category: type,
      mentionCount: 0,
      relevanceTotal: 0,
      latestPublishedAt: 0,
    };

    current.mentionCount += 1;
    current.relevanceTotal += Number(article.relevanceScore || 0);
    current.latestPublishedAt = Math.max(
      current.latestPublishedAt,
      new Date(article.publishedAt).getTime() || 0
    );
    topicsByKey.set(key, current);
  };

  articles.forEach((article) => {
    addTopic(article.category, article, 'Category');
    (article.tags || []).slice(0, 4).forEach((tag) => addTopic(tag, article, article.category || 'Topic'));
    (article.entities || []).slice(0, 3).forEach((entity) => addTopic(entity, article, article.category || 'Entity'));
  });

  return Array.from(topicsByKey.values())
    .map((topic) => ({
      ...topic,
      trendScore: Math.min(
        99,
        Math.max(20, Math.round((topic.relevanceTotal / Math.max(topic.mentionCount, 1)) + topic.mentionCount * 4))
      ),
    }))
    .sort((a, b) => (
      b.mentionCount - a.mentionCount
      || b.trendScore - a.trendScore
      || b.latestPublishedAt - a.latestPublishedAt
    ))
    .slice(0, 5);
};

const PanelClass = 'rounded border border-zinc-200 bg-white';

const CATEGORY_FALLBACK_IMAGES = {
  Events: 'https://images.unsplash.com/photo-1511795409834-ef04bbd61622?auto=format&fit=crop&w=1200&q=80',
  Sponsorships: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?auto=format&fit=crop&w=1200&q=80',
  'Creator Economy': 'https://images.unsplash.com/photo-1529156069898-49953e39b3ac?auto=format&fit=crop&w=1200&q=80',
  Music: 'https://images.unsplash.com/photo-1501386761578-eac5c94b800a?auto=format&fit=crop&w=1200&q=80',
  Nightlife: 'https://images.unsplash.com/photo-1506157786151-b8491531f063?auto=format&fit=crop&w=1200&q=80',
  Hospitality: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=1200&q=80',
  'Sports & Live Entertainment': 'https://images.unsplash.com/photo-1517466787929-bc90951d0974?auto=format&fit=crop&w=1200&q=80',
  'Consumer Brands': 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&w=1200&q=80',
  'Youth Culture': 'https://images.unsplash.com/photo-1529156069898-49953e39b3ac?auto=format&fit=crop&w=1200&q=80',
  Community: 'https://images.unsplash.com/photo-1529156069898-49953e39b3ac?auto=format&fit=crop&w=1200&q=80',
  'AI & Digital Identity': 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&w=1200&q=80',
  'Loneliness & Social Health': 'https://images.unsplash.com/photo-1494526585095-c41746248156?auto=format&fit=crop&w=1200&q=80',
  'Urban Life': 'https://images.unsplash.com/photo-1494526585095-c41746248156?auto=format&fit=crop&w=1200&q=80',
  'Venture & Startups': 'https://images.unsplash.com/photo-1517048676732-d65bc937f952?auto=format&fit=crop&w=1200&q=80',
  News: 'https://images.unsplash.com/photo-1495020689067-958852a7765e?auto=format&fit=crop&w=1200&q=80',
};

const getCategoryFallbackImage = (category = 'News') => (
  CATEGORY_FALLBACK_IMAGES[category] || CATEGORY_FALLBACK_IMAGES.News
);

const getArticleTimestamp = (article) => new Date(article.publishedAt).getTime() || 0;

const ArticleImage = ({ article, className = '' }) => {
  const fallbackSrc = getCategoryFallbackImage(article?.category);
  const [src, setSrc] = useState(article?.imageUrl || fallbackSrc);

  useEffect(() => {
    setSrc(article?.imageUrl || fallbackSrc);
  }, [article?.imageUrl, fallbackSrc]);

  return (
    <img
      src={src}
      alt=""
      className={className}
      loading="lazy"
      onError={() => {
        if (src !== fallbackSrc) setSrc(fallbackSrc);
      }}
    />
  );
};

const SkeletonBlock = ({ className }) => (
  <div className={`animate-pulse rounded bg-zinc-100 ${className}`} />
);

const ArticleRowSkeleton = ({ index }) => (
  <article
    aria-hidden="true"
    className="grid gap-4 border-t border-zinc-200 px-4 py-4 md:grid-cols-[minmax(0,1fr)_150px_124px] md:items-center"
  >
    <div className="grid min-w-0 grid-cols-[88px_minmax(0,1fr)] gap-4">
      <SkeletonBlock className="h-20" />
      <div className="min-w-0 py-1">
        <div className="mb-4 flex gap-2">
          <SkeletonBlock className="h-3 w-20" />
          <SkeletonBlock className="h-3 w-14" />
        </div>
        <SkeletonBlock className={`h-4 ${index % 3 === 0 ? 'w-4/5' : 'w-11/12'}`} />
        <SkeletonBlock className="mt-3 h-3 w-full" />
        <SkeletonBlock className="mt-2 h-3 w-2/3" />
      </div>
    </div>
    <div>
      <SkeletonBlock className="h-4 w-24" />
      <SkeletonBlock className="mt-2 h-3 w-28" />
    </div>
    <SkeletonBlock className="h-4 w-20" />
  </article>
);

export const NewsPage = () => {
  const { newsSearchQuery, setNewsSearchQuery } = usePageContext();
  const [initialArticles] = useState(() => readStoredNewsCache());
  const [articles, setArticles] = useState(initialArticles);
  const [isLoadingArticles, setIsLoadingArticles] = useState(initialArticles.length === 0);
  const [articleError, setArticleError] = useState('');
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [savedArticleIds, setSavedArticleIds] = useState(() => readStoredArray(STORAGE_KEYS.saved, []));

  useEffect(() => {
    let isCancelled = false;

    const loadArticles = async () => {
      const result = await fetchNewsArticles({ limit: 80 });
      if (isCancelled) return;

      if (result.success && result.data.length > 0) {
        setArticles(result.data);
        writeStoredNewsCache(result.data);
        setArticleError('');
      } else {
        setArticles([]);
        setArticleError(result.error || 'No live RSS articles are available yet.');
      }

      setIsLoadingArticles(false);
    };

    loadArticles();

    return () => {
      isCancelled = true;
    };
  }, []);

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEYS.saved, JSON.stringify(savedArticleIds));
  }, [savedArticleIds]);

  const enrichedArticles = useMemo(() => (
    articles.map((article) => ({
      ...article,
      isSaved: savedArticleIds.includes(article.id),
    }))
  ), [articles, savedArticleIds]);

  const filteredArticles = useMemo(() => {
    const normalizedQuery = newsSearchQuery.trim().toLowerCase();

    return enrichedArticles
      .filter((article) => {
        const queryMatches = !normalizedQuery || getArticleHaystack(article).includes(normalizedQuery);
        return queryMatches;
      })
      .sort((a, b) => (
        getArticleTimestamp(b) - getArticleTimestamp(a)
        || b.relevanceScore - a.relevanceScore
      ));
  }, [enrichedArticles, newsSearchQuery]);

  const trendingTopics = useMemo(() => buildTrendingTopics(enrichedArticles), [enrichedArticles]);

  const toggleSavedArticle = (articleId) => {
    setSavedArticleIds((current) => (
      current.includes(articleId)
        ? current.filter((id) => id !== articleId)
        : [...current, articleId]
    ));
  };

  const openArticlePreview = (article) => {
    setSelectedArticle(article);
  };

  const renderArticleRow = (article) => (
    <article
      key={article.id}
      className="grid gap-4 border-t border-zinc-200 px-4 py-4 transition-colors hover:bg-[#fcfaf7] md:grid-cols-[minmax(0,1fr)_150px_124px] md:items-center"
    >
      <button
        type="button"
        onClick={() => openArticlePreview(article)}
        className="grid min-w-0 grid-cols-[88px_minmax(0,1fr)] gap-4 text-left"
      >
        <div className="h-20 overflow-hidden rounded bg-zinc-100">
          <ArticleImage
            article={article}
            className="h-full w-full object-cover grayscale transition duration-500 hover:grayscale-0"
          />
        </div>
        <div className="min-w-0">
          <div className="mb-2 flex flex-wrap items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-zinc-500">
            <span>{article.category}</span>
            <span className="h-1 w-1 rounded-full bg-zinc-300" />
            <span>{formatTimeAgo(article.publishedAt)}</span>
          </div>
          <h3 className="line-clamp-2 text-base font-semibold leading-snug text-zinc-900">
            {article.title}
          </h3>
          <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-zinc-600">
            {article.summary}
          </p>
        </div>
      </button>

      <div className="flex min-w-0 items-center gap-3 text-sm text-zinc-700 md:block">
        <span className="md:hidden text-[10px] font-semibold uppercase tracking-[0.16em] text-zinc-500">Source</span>
        <div className="truncate font-medium">{article.source}</div>
        <div className="truncate text-xs text-zinc-500">{article.sourceDomain}</div>
      </div>

      <div className="flex items-center gap-2 text-sm text-zinc-700">
        <Globe2 size={14} className="text-zinc-500" />
        <span>{article.region}</span>
      </div>
    </article>
  );

  const renderFeedView = () => (
    <section className={`overflow-hidden ${PanelClass}`} aria-busy={isLoadingArticles}>
      <div className="hidden grid-cols-[minmax(0,1fr)_150px_124px] border-b border-zinc-200 px-4 py-3 text-[10px] font-semibold uppercase tracking-[0.16em] text-zinc-500 md:grid">
        <span>Article</span>
        <span>Source</span>
        <span>Region</span>
      </div>

      {isLoadingArticles ? (
        Array.from({ length: 8 }, (_, index) => (
          <ArticleRowSkeleton key={index} index={index} />
        ))
      ) : filteredArticles.length > 0 ? (
        filteredArticles.map(renderArticleRow)
      ) : (
        <div className="px-6 py-20 text-center">
          <p className="text-lg font-semibold text-zinc-900">
            {articleError ? 'No live articles loaded.' : 'No articles match this view.'}
          </p>
          <p className="mt-2 text-sm text-zinc-500">
            {articleError || 'Try a broader category, region, or search term.'}
          </p>
        </div>
      )}
    </section>
  );

  return (
    <div className="min-h-screen bg-[#fcfaf7] pt-16 pb-24 text-zinc-900 selection:bg-orange-500 selection:text-white sm:pt-20">
      <main className="mx-auto max-w-7xl px-6 py-8">
        <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_340px]">
          <div>
            {renderFeedView()}
          </div>

          <aside className="space-y-5 xl:sticky xl:top-36 xl:self-start">
            <section className={`${PanelClass} p-4`}>
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500">Trending Topics</h2>
                <TrendingUp size={15} className="text-zinc-500" />
              </div>
              <div className="space-y-3">
                {isLoadingArticles ? (
                  Array.from({ length: 5 }, (_, index) => (
                    <div key={index} className="grid grid-cols-[24px_minmax(0,1fr)_44px] items-center gap-3">
                      <SkeletonBlock className="h-4 w-4" />
                      <span>
                        <SkeletonBlock className="h-4 w-32" />
                        <SkeletonBlock className="mt-2 h-3 w-24" />
                      </span>
                      <SkeletonBlock className="h-7 w-10" />
                    </div>
                  ))
                ) : trendingTopics.length > 0 ? (
                  trendingTopics.map((topic, index) => {
                    const isActive = newsSearchQuery.trim().toLowerCase() === topic.query.toLowerCase();

                    return (
                      <button
                        key={topic.id}
                        type="button"
                        onClick={() => {
                          setNewsSearchQuery(isActive ? '' : topic.query);
                        }}
                        className={`grid w-full grid-cols-[24px_minmax(0,1fr)_auto] items-center gap-3 rounded px-2 py-1.5 text-left transition ${
                          isActive ? 'bg-zinc-900 text-white' : 'hover:bg-[#fcfaf7]'
                        }`}
                      >
                        <span className={`text-xs font-semibold ${isActive ? 'text-zinc-400' : 'text-zinc-400'}`}>{index + 1}</span>
                        <span className="min-w-0">
                          <span className={`block truncate text-sm font-semibold ${isActive ? 'text-white' : 'text-zinc-900'}`}>{topic.name}</span>
                          <span className={`block text-xs ${isActive ? 'text-zinc-400' : 'text-zinc-500'}`}>
                            {topic.category} - {topic.mentionCount} mentions
                          </span>
                        </span>
                        <span className={`rounded border px-2 py-1 text-xs font-semibold ${
                          isActive ? 'border-white/20 bg-white/10 text-white' : 'border-zinc-200 bg-[#fcfaf7] text-zinc-700'
                        }`}>
                          {topic.trendScore}
                        </span>
                      </button>
                    );
                  })
                ) : (
                  <p className="text-sm leading-relaxed text-zinc-500">
                    Topics will appear once live articles are loaded.
                  </p>
                )}
              </div>
            </section>
          </aside>
        </div>
      </main>

      {selectedArticle && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-zinc-950/50 px-4 py-8 backdrop-blur-sm">
          <article className="relative w-full max-w-3xl overflow-hidden rounded border border-zinc-200 bg-[#fcfaf7] shadow-2xl">
            <button
              type="button"
              onClick={() => setSelectedArticle(null)}
              className="absolute right-4 top-4 z-10 inline-flex h-9 w-9 items-center justify-center rounded border border-zinc-200 bg-white/90 text-zinc-600 transition hover:border-zinc-900 hover:text-zinc-900"
              aria-label="Close article preview"
            >
              <X size={16} />
            </button>

            <div className="aspect-[16/7] bg-zinc-100">
              <ArticleImage article={selectedArticle} className="h-full w-full object-cover" />
            </div>

            <div className="p-5 md:p-7">
              <div className="mb-4 flex flex-wrap items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-zinc-500">
                <span>{selectedArticle.source}</span>
                <span className="h-1 w-1 rounded-full bg-zinc-300" />
                <span>{formatDate(selectedArticle.publishedAt)}</span>
                <span className="h-1 w-1 rounded-full bg-zinc-300" />
                <span>{selectedArticle.category}</span>
              </div>

              <h1 className="text-3xl font-semibold leading-tight tracking-tight text-zinc-900 md:text-4xl">
                {selectedArticle.title}
              </h1>
              <p className="mt-4 text-base leading-relaxed text-zinc-700">
                {selectedArticle.summary}
              </p>

              <div className="mt-5 flex flex-wrap gap-2">
                {(selectedArticle.tags || []).map((tag) => (
                  <span key={tag} className="rounded border border-zinc-200 bg-white px-2.5 py-1 text-xs text-zinc-700">
                    {tag}
                  </span>
                ))}
              </div>

              <div className="mt-6 grid gap-3 border-y border-zinc-200 py-4 sm:grid-cols-3">
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-zinc-500">Relevance</p>
                  <p className="mt-1 text-xl font-semibold text-zinc-900">{selectedArticle.relevanceScore}</p>
                </div>
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-zinc-500">Sentiment</p>
                  <p className="mt-1 text-xl font-semibold text-zinc-900">{selectedArticle.sentimentScore > 0.6 ? 'Positive' : 'Mixed'}</p>
                </div>
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-zinc-500">Read Time</p>
                  <p className="mt-1 text-xl font-semibold text-zinc-900">{selectedArticle.readTime}</p>
                </div>
              </div>

              <div className="mt-6">
                <p className="mb-3 text-xs font-semibold uppercase tracking-[0.14em] text-zinc-500">Related Articles</p>
                <div className="space-y-2">
                  {(selectedArticle.relatedIds || [])
                    .map((id) => enrichedArticles.find((article) => article.id === id))
                    .filter(Boolean)
                    .map((article) => (
                      <button
                        key={article.id}
                        type="button"
                        onClick={() => openArticlePreview(article)}
                        className="block w-full rounded border border-zinc-200 bg-white px-3 py-2 text-left transition hover:border-zinc-900"
                      >
                        <span className="line-clamp-1 text-sm font-semibold text-zinc-900">{article.title}</span>
                        <span className="mt-1 block text-xs text-zinc-500">{article.source} - {formatTimeAgo(article.publishedAt)}</span>
                      </button>
                    ))}
                </div>
              </div>

              <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                <a
                  href={selectedArticle.originalUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded bg-zinc-900 px-4 py-3 text-xs font-bold uppercase tracking-[0.14em] text-white transition hover:bg-zinc-700"
                >
                  <ExternalLink size={14} />
                  Open Original Source
                </a>
                <button
                  type="button"
                  onClick={() => toggleSavedArticle(selectedArticle.id)}
                  className="inline-flex items-center justify-center gap-2 rounded border border-zinc-300 px-4 py-3 text-xs font-bold uppercase tracking-[0.14em] text-zinc-700 transition hover:border-zinc-900 hover:text-zinc-900"
                >
                  <Bookmark size={14} fill={savedArticleIds.includes(selectedArticle.id) ? 'currentColor' : 'none'} />
                  {savedArticleIds.includes(selectedArticle.id) ? 'Saved' : 'Save Article'}
                </button>
              </div>
            </div>
          </article>
        </div>
      )}
    </div>
  );
};
